/**
 * Плитки справочников — ОДИН код оформления на все разделы.
 *
 * «Клиники» и «Врачи» устроены одинаково не случайно: это два взгляда на одну
 * связь (§3), и работа с ними идёт вперемешку. Разное оформление заставляло бы
 * каждый раз заново соображать, где здесь кнопка правки и что значит цвет.
 *
 * Поэтому здесь собраны все общие части: сетка, плитка с подвалом действий,
 * строка-факт с иконкой, пилюли связей, метки-счётчики и строка справки для
 * режима просмотра. Разделы отличаются только содержимым, но не языком.
 *
 * Правило цвета, общее для всех плиток:
 *   серый  — обычные сведения, в том числе незаполненные;
 *   синий  — особый порядок работы, выбранный осознанно;
 *   жёлтый — включённое напоминание;
 *   красный — только то, из-за чего запись нерабочая.
 * Красным нельзя помечать «не заполнено»: пустых полей много, и подсветка
 * каждого превращает тревожный цвет в фоновый узор.
 */

import type { ReactNode } from 'react';
import { сенсорный } from './pointer.js';
import { Pencil, Trash2 } from 'lucide-react';

/**
 * Иконка из `lucide-react`.
 *
 * Описана структурно, а не через её собственный тип: так же объявлена иконка
 * у `SectionHeader`, и раздел не обязан ничего знать про библиотеку иконок.
 */
type CardIcon = (props: { size?: number | string; className?: string }) => ReactNode;

/** Сетка плиток. Число колонок растёт с шириной, но плитка не растягивается. */
export function CardGrid({ children, compact = false }: { children: ReactNode; compact?: boolean }) {
  return <div className={compact ? 'directory-grid-compact grid auto-rows-fr items-stretch gap-3' : 'grid auto-rows-fr items-stretch gap-2 sm:grid-cols-2 sm:gap-3 xl:grid-cols-3 2xl:grid-cols-4'}>{children}</div>;
}

/**
 * Плитка справочника: тело открывает карточку, подвал держит действия.
 *
 * Действия вынесены в подвал отдельными кнопками, а не спрятаны в тело: щелчок
 * по телу открывает просмотр, и удалять оттуда же было бы опасно. Кнопка
 * удаления спокойная и краснеет при наведении — красной на каждой из трёх
 * десятков плиток она читалась как узор, а не как предупреждение.
 */
export function Card({
  title,
  icon: Icon,
  subtitle,
  children,
  onOpen,
  onEdit,
  onDelete,
  deleteTitle,
  deleteLabel = 'Удалить',
  editLabel = 'Изменить',
  extraAction,
}: {
  title: string;
  icon?: CardIcon;
  subtitle?: string;
  children: ReactNode;
  /** `null` — по телу плитки открывать нечего: внутри свои ссылки и поля. */
  onOpen: (() => void) | null;
  /** Нет — на плитке не правят: карточка открывается и правится в окне. */
  onEdit?: () => void;
  /** `null` — удалять нечего: кнопка не показывается вовсе. */
  onDelete: (() => void) | null;
  deleteTitle: string;
  /**
   * Подпись кнопки удаления.
   *
   * У договоров она стирает не запись, а номер с датой, и «Удалить» там
   * читалось бы как «удалить юрлицо» — не то действие, за которое берутся
   * в списке договоров.
   */
  deleteLabel?: string;
  editLabel?: string;
  /** Третье действие раздела: скрепка у договоров. */
  extraAction?: { icon: CardIcon; title: string; onClick: () => void; disabled?: boolean };
}) {
  return (
    <div
      className="directory-card group relative flex flex-col overflow-hidden rounded-2xl border transition-shadow hover:shadow-md"
      style={{ borderColor: 'var(--otc-line)', background: 'var(--otc-surface)' }}
    >
      {/*
        Действия — ЗНАЧКАМИ В ПРАВОМ ВЕРХНЕМ УГЛУ.

        Нижняя полоса с «Изменить» и «Удалить» занимала около сорока точек —
        четверть высоты плитки, — и при шести десятках врачей это добрая треть
        списка, отданная кнопкам, которыми пользуются изредка. Значка
        достаточно: корзина узнаётся без подписи.

        Где правка живёт в самой карточке (врачи, клиники), карандаша на плитке
        нет вовсе, а полоса снизу не рисуется — остаётся одна корзина в углу.
        Где плитку правят на месте, всё остаётся как было.

        Кнопки стоят ПОВЕРХ плитки, а не внутри тела: тело — само кнопка
        открытия, а кнопку в кнопку вкладывать нельзя.
      */}
      <div className={`absolute right-1.5 top-1.5 z-10 flex gap-1 ${onEdit ? 'md:hidden' : ''}`}>
        {onEdit && (
          <button
            onClick={onEdit}
            className="rounded-lg border p-1.5"
            style={{ borderColor: 'var(--otc-line)', color: 'var(--otc-ink)', background: 'var(--otc-surface)' }}
            title="Изменить"
            aria-label="Изменить"
          >
            <Pencil size={13} />
          </button>
        )}
        {extraAction && (
          <button
            onClick={extraAction.onClick}
            disabled={extraAction.disabled}
            className="rounded-lg border p-1.5 text-ink2 disabled:opacity-40"
            style={{ borderColor: 'var(--otc-line)', background: 'var(--otc-surface)' }}
            title={extraAction.title}
            aria-label={extraAction.title}
          >
            <extraAction.icon size={13} />
          </button>
        )}
        {onDelete && (
          <button
            onClick={onDelete}
            className="rounded-lg border p-1.5 text-ink2"
            style={{ borderColor: 'var(--otc-line)', background: 'var(--otc-surface)' }}
            title={deleteTitle}
            aria-label={deleteTitle}
          >
            <Trash2 size={13} />
          </button>
        )}
      </div>

      {/*
        Тело — КНОПКА ТОЛЬКО ЕСЛИ ЕСТЬ ЧТО ОТКРЫВАТЬ.

        В договорах внутри плитки живут свои ссылки и поля: имя приложенного
        файла — ссылка на скачивание, крестик рядом удаляет файл, а в режиме
        правки там стоят поля ввода. Всё это лежало ВНУТРИ кнопки, и щелчок
        по ссылке всплывал до неё: вместо скачивания открывалась правка
        карточки. Кнопку в кнопку вкладывать нельзя и по разметке.

        Когда `onOpen` не задан, тело остаётся обычным блоком, а правка
        открывается кнопкой «Изменить» — она никуда не делась.
      */}
      {/*
        МЫШЬЮ — ДВОЙНОЙ ЩЕЛЧОК, пальцем — обычное касание.

        Одиночный щелчок открывал карточку и тогда, когда по плитке просто
        целились: выделить телефон, попасть в кнопку. В журнале заказов
        карточка открывается двойным щелчком, и здесь теперь так же — заодно
        повторный двойной по той же плитке её закрывает.

        На телефоне двойное касание — это «приблизить», а не «открыть»,
        поэтому там остаётся одно касание.
      */}
      {onOpen ? (
        <button
          onClick={сенсорный() ? onOpen : undefined}
          onDoubleClick={сенсорный() ? undefined : onOpen}
          className="directory-card-body min-w-0 flex flex-1 flex-col items-stretch justify-start p-4 text-left"
          title={сенсорный() ? 'Открыть карточку' : 'Двойной щелчок — открыть карточку'}
        >
          <div className="directory-card-heading">
            {Icon && <span className="directory-card-symbol" aria-hidden="true"><Icon size={20} /></span>}
            <div className="min-w-0"><p className="font-display text-base font-semibold leading-snug text-ink">{title}</p>
              {subtitle && <p className="mt-1 text-xs leading-relaxed text-ink2">{subtitle}</p>}
            </div>
          </div>
          {children}
        </button>
      ) : (
        <div className="directory-card-body min-w-0 flex flex-1 flex-col items-stretch justify-start p-4 text-left">
          <div className="directory-card-heading">
            {Icon && <span className="directory-card-symbol" aria-hidden="true"><Icon size={20} /></span>}
            <div className="min-w-0"><p className="font-display text-base font-semibold leading-snug text-ink">{title}</p>
              {subtitle && <p className="mt-1 text-xs leading-relaxed text-ink2">{subtitle}</p>}
            </div>
          </div>
          {children}
        </div>
      )}

      {/* Нижняя полоса — только там, где плитку правят на месте. */}
      {onEdit && (
        <div
          className="directory-card-actions hidden flex-wrap items-center gap-2 border-t px-3 py-2 md:flex md:px-4 md:py-3"
          style={{ borderColor: 'var(--otc-line)', background: 'var(--otc-bg)' }}
        >
          <button
            onClick={onEdit}
            className="inline-flex items-center gap-1.5 rounded-lg border px-2 py-1 text-[11px] font-medium transition-colors hover:bg-black/5 md:px-3 md:py-1.5 md:text-xs"
            style={{ borderColor: 'var(--otc-line)', color: 'var(--otc-ink)', background: 'var(--otc-surface)' }}
          >
            <Pencil size={13} /> {editLabel}
          </button>

          {extraAction && (
            <button
              onClick={extraAction.onClick}
              disabled={extraAction.disabled}
              className="inline-flex items-center gap-1.5 rounded-lg border px-2 py-1 text-[11px] font-medium transition-colors hover:bg-black/5 disabled:opacity-40 md:px-3 md:py-1.5 md:text-xs"
              style={{ borderColor: 'var(--otc-line)', color: 'var(--otc-ink)', background: 'var(--otc-surface)' }}
              title={extraAction.title}
            >
              <extraAction.icon size={13} /> {extraAction.title}
            </button>
          )}

          {onDelete && (
            <button
              onClick={onDelete}
              className="ml-auto inline-flex items-center gap-1.5 rounded-lg border px-2 py-1 text-[11px] font-medium text-ink2 transition-colors hover:border-[color:var(--otc-coral)] hover:text-[color:var(--otc-coral)] md:px-3 md:py-1.5 md:text-xs"
              style={{ borderColor: 'var(--otc-line)', background: 'var(--otc-surface)' }}
              title={deleteTitle}
            >
              <Trash2 size={13} /> {deleteLabel}
            </button>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * Строка-факт на плитке: иконка и значение.
 *
 * Пустое значение показано серым текстом-заменителем, а не пропущено: «почта
 * не указана» — это сведение, а отсутствие строки читается как недосмотр.
 */
export function CardFact({
  icon: Icon,
  prefix,
  value,
  empty,
  breakAll,
}: {
  icon: CardIcon;
  /** Уточнение перед значением — название филиала или юрлица. */
  prefix?: string;
  value: string;
  /** Текст на месте пустого значения. */
  empty: string;
  /** Для почты и длинных адресов: разрешить перенос внутри слова. */
  breakAll?: boolean;
}) {
  return (
    <p className="flex items-start gap-2 text-xs leading-relaxed text-ink2">
      <Icon size={12} className="mt-0.5 flex-shrink-0 opacity-60" />
      <span className={`min-w-0${breakAll ? ' break-all' : ''}`}>
        {prefix && <span className="opacity-70">{prefix}: </span>}
        {value || <span className="opacity-60">{empty}</span>}
      </span>
    </p>
  );
}

/**
 * Пилюли связей с иконкой раздела: врачи клиники, клиники врача.
 *
 * Хвост «+N» вместо переноса всего списка: связей обычно единицы, но одна
 * запись с двумя десятками связей не должна растягивать всю строку сетки.
 */
export function CardLinks({
  icon: Icon,
  names,
  empty,
  limit = 6,
}: {
  icon: CardIcon;
  names: string[];
  empty: string;
  limit?: number;
}) {
  return (
    <div className="mt-1.5 flex flex-wrap items-center gap-1 md:mt-2.5 md:gap-1.5">
      {names.length === 0 && <Icon size={14} className="flex-shrink-0 text-ink2 opacity-60" />}
      {names.slice(0, limit).map((name) => (
        <span
          key={name}
          className="inline-flex max-w-full items-center gap-1.5 rounded-full border px-2 py-1 text-xs text-ink"
          style={{ borderColor: 'var(--otc-line)', background: 'var(--otc-bg)' }}
        >
          <Icon size={13} className="shrink-0 text-ink2" /><span className="min-w-0 break-words">{name}</span>
        </span>
      ))}
      {names.length > limit && (
        <span className="text-[11px] text-ink2">+{names.length - limit}</span>
      )}
      {names.length === 0 && <span className="text-xs text-ink2 opacity-60">{empty}</span>}
    </div>
  );
}

/** Полка меток-счётчиков внизу плитки. */
export function CardChips({ children }: { children: ReactNode }) {
  return <div className="directory-card-statuses">{children}</div>;
}

/** Метка-счётчик. Цвет — по общему правилу раздела (см. шапку файла). */
export function Chip({
  children,
  danger,
  warn,
  info,
  color: customColor,
  icon: Icon,
}: {
  children: ReactNode;
  danger?: boolean;
  warn?: boolean;
  info?: boolean;
  color?: string;
  icon?: CardIcon;
}) {
  const color = danger
    ? 'var(--otc-coral)'
    : warn
      ? 'var(--otc-amber)'
      : info
        ? 'var(--otc-blue)'
        : 'var(--otc-ink2)';
  return (
    <span className="inline-flex max-w-full items-center gap-1.5 rounded-md px-2 py-1 text-xs leading-snug" style={{ background: 'var(--otc-bg)', color: customColor ?? color }}>
      {Icon && <Icon size={14} className="shrink-0" />}
      {children}
    </span>
  );
}

/**
 * Строка справки в режиме просмотра: подпись слева, значение справа.
 *
 * На телефоне подпись встаёт НАД значением, а не рядом.
 *
 * Раньше подпись занимала жёсткие 128 точек при любой ширине экрана, и
 * значению оставалось около двухсот. Этого хватало «Иркутску», но не
 * «Обществу с ограниченной ответственностью» — оно рассыпалось на три
 * строки, — и совсем не хватало расчётному счёту: двадцать цифр подряд для
 * браузера одно слово, переносить его негде, и хвост попросту уезжал за край
 * окна. Реквизит, который нельзя прочитать, хуже незаполненного: его не
 * видно, что он обрезан.
 *
 * `break-words` разрешает разорвать длинное число там, где иначе перенос
 * невозможен, а `min-w-0` снимает с колонки запрет сжиматься — без него
 * flex не даст переносу сработать вовсе.
 */
export function Row({
  label,
  value,
  muted,
  strong,
  href,
}: {
  label: string;
  value: string;
  muted?: boolean;
  /**
   * Жирно — то, по чему связываются: телефон, ник в Telegram. В карточке
   * их ищут глазами первыми, а набранные обычным весом они терялись среди
   * реквизитов.
   */
  strong?: boolean;
  /**
   * Ссылка — почта: щелчок открывает новое письмо на этот адрес, и цвет
   * ссылки сразу говорит, что по нему можно щёлкнуть.
   */
  href?: string;
}) {
  const empty = !value.trim();
  const вес = strong || href ? 'font-semibold' : '';
  return (
    <div className="flex flex-col py-0.5 text-xs sm:flex-row sm:gap-2">
      <span className="text-ink2 sm:w-36 sm:flex-shrink-0">{label}</span>
      {!empty && href ? (
        <a
          href={href}
          className={`min-w-0 break-words underline-offset-2 hover:underline ${вес}`}
          style={{ color: 'var(--otc-blue)' }}
        >
          {value}
        </a>
      ) : (
        <span
          className={`min-w-0 break-words ${muted ? 'tabular' : ''} ${empty ? '' : вес}`}
          style={{ color: empty ? 'var(--otc-lightred)' : 'var(--otc-ink)' }}
        >
          {empty ? 'не заполнено' : value}
        </span>
      )}
    </div>
  );
}

/**
 * Кнопка добавления вложенной записи: юрлица, филиала, врача.
 *
 * Полноценная кнопка, а не текстовая ссылка «+юрлицо»: добавление — обычное
 * действие карточки и должно выглядеть нажимаемым.
 */
export function AddButton({
  onClick,
  children,
  accent = 'var(--otc-accent)',
}: {
  onClick: () => void;
  children: ReactNode;
  accent?: string;
}) {
  return (
    <button
      onClick={onClick}
      className="inline-flex items-center gap-1.5 rounded-lg border px-2.5 py-1.5 text-xs font-medium transition-colors hover:bg-black/5"
      style={{ borderColor: accent, color: accent, background: 'transparent' }}
    >
      {children}
    </button>
  );
}

/**
 * Пометка в открытой карточке: способ оплаты и напоминание у юрлица,
 * уведомления у врача.
 *
 * Форма у всех одна — рамка, скругление, мелкий шрифт; цвет говорит о смысле:
 * серый — как есть, жёлтый — про что не забыть, синий — особый порядок в
 * документах. Голый цветной текст рядом с названием читался как случайная
 * подпись, а не как свойство записи. Общая для клиник и врачей, чтобы одно
 * и то же свойство выглядело одинаково в обеих карточках.
 */
export function Пометка({ цвет, children }: { цвет: string; children: ReactNode }) {
  return (
    <span
      className="rounded border px-1.5 py-0.5 text-[11px]"
      style={{
        color: цвет,
        borderColor: `color-mix(in srgb, ${цвет} 35%, transparent)`,
        background: `color-mix(in srgb, ${цвет} 8%, transparent)`,
      }}
    >
      {children}
    </span>
  );
}
